import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import KpiCard from "./KpiCard";
import Chip from "./Chip";
import { dateLabel, monthLabel, money } from "./money";

function sourceLabel(source) {
  if (!source) return "Missing";
  if (source === "revenue_trailing_actual") return "Trailing actual";
  if (source === "revenue_unapproved_fallback") return "Unapproved fallback";
  if (source.startsWith("budget:")) return "Budget";
  if (source === "forecast_planning") return "Forecast Planning";
  if (source === "revenue_lookback" || source === "lookback_fallback")
    return "Unapproved fallback";
  if (source.startsWith("revenue_target:")) return "Planned target";
  return source;
}

export default function RevenuePlanTab({ data, loading, error, onRefresh }) {
  if (loading)
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading revenue plan…
      </div>
    );
  if (error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  if (!data)
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
        No revenue plan is available for this horizon.
      </div>
    );
  const lookback = data.lookback || {};
  const months = data.months || [];
  const modelLabel =
    data.distributionMode === "weekday_weights"
      ? `Weekday weights (÷${data.workdaysPerMonth || 22}, ${Math.round(
          (data.channelMix?.shopify || 0.85) * 100,
        )}/${Math.round((data.channelMix?.amazon || 0.15) * 100)})`
      : "Sale date plus settlement lag";
  const trailingLabel =
    data.distributionMode === "weekday_weights" && data.trailingActual
      ? `First ${data.trailingAvgWeeks || 4} weeks: trailing ${
        data.trailingAvgDays || data.trailingActual.windowDays || 14
      }-day actuals (${money(data.trailingActual.dailyBase)}/day)`
      : "";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-white sm:grid-cols-4">
        <KpiCard
          label={`Trailing ${lookback.days || 0}-day Shopify avg`}
          value={lookback.perChannelDailyAvg?.shopify}
          moneyValue
          tone="text-emerald-700"
        />
        <KpiCard
          label={`Trailing ${lookback.days || 0}-day Amazon avg`}
          value={lookback.perChannelDailyAvg?.amazon}
          moneyValue
          tone="text-emerald-700"
        />
        <KpiCard
          label="Near-term daily forecast"
          value={
            data.dailyRevenue
              ?.slice(0, 14)
              .reduce((sum, row) => sum + Number(row.total || 0), 0) / 14
          }
          moneyValue
        />
        <KpiCard
          label="Months with targets"
          value={`${months.filter((month) => month.hasTarget).length}/${months.length}`}
        />
      </div>
      <p className="text-sm text-gray-600">
        Plan:{" "}
        <span className="font-semibold text-gray-800">
          {data.planLabel || "Unlabeled"}
        </span>
        {" · "}Model: {modelLabel}
        {trailingLabel ? ` · ${trailingLabel}` : ""}
      </p>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-3 py-2">
          <div>
            <p className="text-lg font-semibold text-gray-900">
              Monthly revenue targets
            </p>
            <p className="text-xs text-gray-500">
              Monthly targets and source traceability for this forecast horizon.
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
        <div className="px-3 pb-2">
          <p className="mt-1 text-xs text-gray-500">
            {data.distributionMode === "weekday_weights"
              ? "Weekday weights allocate deposits directly on each forecast date."
              : `Deposits = sale date + ${
                  data.settlement?.shopify ?? 2
                } business days (Shopify ${
                  data.settlement?.shopify ?? 2
                }, Amazon ${
                  data.settlement?.amazon ?? 10
                }); weekends and bank holidays skipped.`}
          </p>
        </div>
        <table className="min-w-[750px] w-full text-sm">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="px-3 py-2">Month</th>
              <th className="px-3 py-2 text-right">Shopify</th>
              <th className="px-3 py-2 text-right">Amazon</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Plan source</th>
              <th className="px-3 py-2 text-right">Plan total</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => (
              <tr key={month.month} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-900">
                  {monthLabel(month.month)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {money(month.shopifyTarget)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {money(month.amazonTarget)}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {money(month.totalTarget)}
                </td>
                <td className="px-3 py-2">
                  <Chip
                    tone={
                      month.unapproved
                        ? "warning"
                        : month.planSource
                        ? "success"
                        : "critical"
                    }
                  >
                    {sourceLabel(
                      month.unapproved
                        ? "revenue_unapproved_fallback"
                        : month.planSource,
                    )}
                  </Chip>
                  {month.warning && (
                    <p className="mt-1 text-xs text-amber-700">
                      {month.warning}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {money(month.planTotal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-3 py-2">
          <p className="text-lg font-semibold text-gray-900">
            Daily revenue output
          </p>
          <p className="text-xs text-gray-500">
            Channel-level collection allocation across the model horizon.
          </p>
        </div>
        <table className="min-w-[700px] w-full text-sm">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 text-right">Shopify</th>
              <th className="px-3 py-2 text-right">Amazon</th>
              <th className="px-3 py-2 text-right">Other</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {(data.dailyRevenue || []).map((row) => (
              <tr key={row.date} className="border-t border-gray-100">
                <td className="px-3 py-2 tabular-nums">
                  {dateLabel(row.date)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {money(row.shopify)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {money(row.amazon)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {money(row.other)}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {money(row.total)}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {sourceLabel(row.source)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
